/**
 * ============================================================================
 * CourtFormModal - ADD/EDIT COURT MODAL FORM
 * ============================================================================
 *
 * Replaces 270+ line inline form (old lines 400-670)
 * Features:
 * - Add or Edit mode
 * - Single form state object (not 10 separate useState)
 * - Comprehensive validation
 * - Recurring discount inputs
 * - Discount examples
 * - Escape key support
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { RecurringDiscountInputs } from './RecurringDiscountInputs';
import { DiscountExamples } from './DiscountExamples';
import { validateCourtForm } from '../../utils/courtValidation';
import type { Court, CourtFormData } from '../../types/courts';
import type { ValidationError } from '../../utils/courtValidation';
import Modal from '../ui/Modal';

interface CourtFormModalProps {
  isOpen: boolean;
  court: Court | null; // null = add mode, Court = edit mode
  sports: Array<{ id: number; name: string }>;
  existingCourts: Court[];
  onClose: () => void;
  onSubmit: (formData: CourtFormData, courtId?: number) => void;
  isSaving?: boolean;
}

const initialFormData: CourtFormData = {
  courtName: '',
  courtType: 'Court',
  location: 'Indoor',
  selectedSport: '',
  isActive: true,
  customHourlyRate: '',
  customPerPersonRate: '',
  minPlayers: '',
  maxPlayers: '',
  pricingMode: '',
  allowSharedBooking: false,
  perPersonDurationMode: 'timed',
  minBookingDurationHours: '',
  bookingSlotIncrementHours: '',
  allowRecurringBookings: true,
  discount1Month: '',
  discount3Months: '',
  discount6Months: '',
  discount12Months: '',
  pricingSlabs: [],
  parentCourtId: null,
  shareType: 'standalone',
  isVirtual: false,
  maxConcurrentChildren: '',
  physicalCourtId: '',
};

/**
 * Court add/edit form modal
 */
export function CourtFormModal({
  isOpen,
  court,
  sports,
  existingCourts,
  onClose,
  onSubmit,
  isSaving = false,
}: CourtFormModalProps) {
  const [formData, setFormData] = useState<CourtFormData>(initialFormData);
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const isEditMode = !!court;

  // Load court data when editing
  useEffect(() => {
    if (court) {
      setFormData({
        courtName: court.name, // Use existing court name
        courtType: court.name.includes('Field') ? 'Field' : 'Court',
        location: 'Indoor', // Default for edit mode (will be stored in backend later)
        selectedSport: court.sport_id,
        isActive: court.is_active,
        customHourlyRate: court.custom_hourly_rate ?? '',
        customPerPersonRate: court.custom_per_person_rate ?? '',
        minPlayers: court.min_players ?? '',
        maxPlayers: court.max_players ?? '',
        pricingMode: court.pricing_mode ?? '',
        allowSharedBooking: court.allow_shared_booking ?? false,
        perPersonDurationMode: court.per_person_duration_mode ?? 'timed',
        minBookingDurationHours: court.min_booking_duration_hours ?? '',
        bookingSlotIncrementHours: court.booking_slot_increment_hours ?? '',
        allowRecurringBookings: court.allow_recurring_bookings ?? true,
        discount1Month: court.recurring_discount_1_month ?? '',
        discount3Months: court.recurring_discount_3_months ?? '',
        discount6Months: court.recurring_discount_6_months ?? '',
        discount12Months: court.recurring_discount_12_months ?? '',
        pricingSlabs: court.pricing_slabs ?? [],
        parentCourtId: court.parent_court_id ?? null,
        shareType: court.share_type ?? 'standalone',
        isVirtual: court.is_virtual ?? false,
        maxConcurrentChildren: court.max_concurrent_children ?? '',
        physicalCourtId: court.physical_court_id ?? '',
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors([]);
  }, [court, isOpen]);

  const handleChange = (field: keyof CourtFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    setErrors((prev) => prev.filter((e) => e.field !== field));
  };

  const handleDiscountChange = (field: string, value: number | '') => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => prev.filter((e) => e.field !== field));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateCourtForm(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Submit
    onSubmit(formData, court?.id);
  };

  // Convert errors array to object for easier lookup
  const errorMap = errors.reduce((acc, err) => {
    acc[err.field] = err.message;
    return acc;
  }, {} as { [key: string]: string });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" preventClose={isSaving} animate={false}>
        {/* Header - Fixed */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white rounded-t-2xl flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditMode ? `Edit ${court.name}` : 'Add New Court/Field'}
          </h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form - Scrollable */}
        <form id="court-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-md font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Court Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Court Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.courtName}
                  onChange={(e) => handleChange('courtName', e.target.value)}
                  placeholder="e.g., Indoor Court 1, 5-Aside Pitch A, Main Basketball Court"
                  maxLength={50}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errorMap.courtName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <div className="flex items-center justify-between mt-1">
                  {errorMap.courtName && (
                    <p className="text-xs text-red-500">{errorMap.courtName}</p>
                  )}
                  <p className={`text-xs ${formData.courtName.length > 45 ? 'text-amber-600 font-medium' : 'text-gray-500'} ml-auto`}>
                    {formData.courtName.length}/50 characters
                  </p>
                </div>
              </div>

              {/* Court Type - Only for add mode */}
              {!isEditMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="Court"
                        checked={formData.courtType === 'Court'}
                        onChange={(e) => handleChange('courtType', e.target.value as 'Court' | 'Field')}
                        className="mr-2 cursor-pointer"
                      />
                      <span className="text-sm font-medium text-gray-700">Court</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="Field"
                        checked={formData.courtType === 'Field'}
                        onChange={(e) => handleChange('courtType', e.target.value as 'Court' | 'Field')}
                        className="mr-2 cursor-pointer"
                      />
                      <span className="text-sm font-medium text-gray-700">Field</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Location (Indoor/Outdoor) */}
              <div className={!isEditMode ? '' : 'md:col-span-2'}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value as 'Indoor' | 'Outdoor')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Indoor">Indoor</option>
                  <option value="Outdoor">Outdoor</option>
                </select>
              </div>

              {/* Sport Selection */}
              <div className={!isEditMode ? 'md:col-span-1' : 'md:col-span-2'}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sport <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.selectedSport}
                  onChange={(e) => handleChange('selectedSport', e.target.value === '' ? '' : Number(e.target.value))}
                  disabled={isEditMode} // Can't change sport when editing
                  className={`w-full border ${
                    errorMap.selectedSport ? 'border-red-300' : 'border-gray-300'
                  } rounded-lg px-3 py-2 ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select a sport</option>
                  {sports.map((sport) => (
                    <option key={sport.id} value={sport.id}>
                      {sport.name}
                    </option>
                  ))}
                </select>
                {errorMap.selectedSport && (
                  <p className="text-xs text-red-600 mt-1">{errorMap.selectedSport}</p>
                )}
              </div>

              {/* Custom Hourly Rate */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Hourly Rate (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 text-sm">{CURRENCY_SYMBOL}</span>
                  <input
                    type="number"
                    min="0"
                    max="1000000"
                    step="100"
                    value={formData.customHourlyRate}
                    onChange={(e) => handleChange('customHourlyRate', e.target.value === '' ? '' : Number(e.target.value))}
                    className={`w-full border ${
                      errorMap.customHourlyRate ? 'border-red-300' : 'border-gray-300'
                    } rounded-lg pl-8 pr-3 py-2`}
                    placeholder="Leave empty to use sport/venue base rate"
                  />
                </div>
                {errorMap.customHourlyRate && (
                  <p className="text-xs text-red-600 mt-1">{errorMap.customHourlyRate}</p>
                )}
                <p className="text-xs text-gray-600 mt-1">
                  Set a specific rate for this court, or leave empty to use the sport's custom rate or venue base rate.
                </p>
              </div>
            </div>
          </div>

          {/* Slab Pricing */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-md font-semibold text-gray-900 mb-1">📋 Fixed-Duration Pricing (Optional)</h3>
            <p className="text-sm text-gray-500 mb-4">
              Use this when the court is only bookable in specific durations at fixed prices (e.g. 1.5hr for {CURRENCY_SYMBOL}35,000, 2hr for {CURRENCY_SYMBOL}45,000).
              When set, <strong>hourly rate is ignored</strong> and only these exact durations appear to customers.
              Leave empty to use standard hourly pricing.
            </p>
            {formData.pricingSlabs.map((slab, idx) => (
              <div key={idx} className="flex items-center gap-3 mb-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Duration (hours)</label>
                  <input
                    type="number"
                    min="0.5"
                    max="24"
                    step="0.5"
                    value={slab.duration_hours}
                    onChange={(e) => {
                      const updated = [...formData.pricingSlabs];
                      updated[idx] = { ...updated[idx], duration_hours: parseFloat(e.target.value) || 0 };
                      handleChange('pricingSlabs', updated);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="e.g. 1.5"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">Price ({CURRENCY_SYMBOL})</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500 text-sm">{CURRENCY_SYMBOL}</span>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={slab.price}
                      onChange={(e) => {
                        const updated = [...formData.pricingSlabs];
                        updated[idx] = { ...updated[idx], price: parseInt(e.target.value) || 0 };
                        handleChange('pricingSlabs', updated);
                      }}
                      className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm"
                      placeholder="e.g. 35000"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    handleChange('pricingSlabs', formData.pricingSlabs.filter((_, i) => i !== idx));
                  }}
                  className="mt-5 text-red-500 hover:text-red-700 text-sm px-2"
                  title="Remove slab"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => handleChange('pricingSlabs', [...formData.pricingSlabs, { duration_hours: 1, price: 0 }])}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              + Add duration option
            </button>
          </div>

          {/* Pricing Configuration */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-md font-semibold text-gray-900 mb-4">💰 Pricing Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Pricing Mode */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pricing Mode
                </label>
                <select
                  value={formData.pricingMode}
                  onChange={(e) => handleChange('pricingMode', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Inherit from venue default</option>
                  <option value="hourly">Hourly Only</option>
                  <option value="per_person">Per Person Only</option>
                  <option value="both">Both (Customer can choose)</option>
                </select>
                <p className="text-xs text-gray-600 mt-1">
                  Choose how customers pay for this court. "Both" lets them pick between hourly or per-person pricing.
                </p>
              </div>

              {/* Custom Per-Person Rate */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Per-Person Rate (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500 text-sm">{CURRENCY_SYMBOL}</span>
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    step="100"
                    value={formData.customPerPersonRate}
                    onChange={(e) => handleChange('customPerPersonRate', e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2"
                    placeholder="Leave empty to use venue base rate"
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Rate charged per person per hour. Used when pricing mode is "Per Person" or "Both".
                </p>
              </div>

              {/* Min Players */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Players (optional)
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={formData.minPlayers}
                  onChange={(e) => handleChange('minPlayers', e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 2"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Minimum players required for per-person bookings
                </p>
              </div>

              {/* Max Players */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Players (optional)
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={formData.maxPlayers}
                  onChange={(e) => handleChange('maxPlayers', e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 10"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Maximum players allowed for per-person bookings
                </p>
              </div>

              {/* Allow Shared Booking Toggle - only for per_person or both pricing modes */}
              {(formData.pricingMode === 'per_person' || formData.pricingMode === 'both') && (
                <div className="md:col-span-2 mt-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allowSharedBooking}
                        onChange={(e) => handleChange('allowSharedBooking', e.target.checked)}
                        className="mt-0.5 rounded border-gray-300"
                      />
                      <div>
                        <span className="font-medium text-gray-900">Allow Shared Booking (Drop-in)</span>
                        <p className="text-sm text-gray-600 mt-1">
                          Multiple customers can independently book this court at the same time.
                          The court stays available until the maximum player capacity is reached.
                          Each player pays per-person and books separately.
                        </p>
                        {!formData.maxPlayers && formData.allowSharedBooking && (
                          <p className="text-sm text-amber-700 mt-2 font-medium">
                            Please set Max Players above — shared booking requires a capacity limit.
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Per-Person Duration Mode - only for per_person or both pricing modes */}
              {(formData.pricingMode === 'per_person' || formData.pricingMode === 'both') && (
                <div className="md:col-span-2 mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Per-Person Duration Mode
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.perPersonDurationMode === 'timed'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="perPersonDurationMode"
                        value="timed"
                        checked={formData.perPersonDurationMode === 'timed'}
                        onChange={() => handleChange('perPersonDurationMode', 'timed')}
                        className="mt-0.5"
                      />
                      <div>
                        <span className="font-medium text-gray-900 text-sm">Timed Sessions</span>
                        <p className="text-xs text-gray-600 mt-0.5">
                          Players select start &amp; end time. Charged per person per hour.
                        </p>
                      </div>
                    </label>
                    <label
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.perPersonDurationMode === 'flexible'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="perPersonDurationMode"
                        value="flexible"
                        checked={formData.perPersonDurationMode === 'flexible'}
                        onChange={() => handleChange('perPersonDurationMode', 'flexible')}
                        className="mt-0.5"
                      />
                      <div>
                        <span className="font-medium text-gray-900 text-sm">Flexible (Play Till Tired)</span>
                        <p className="text-xs text-gray-600 mt-0.5">
                          Players select only start time. Flat per-person rate, no end time needed.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Basic Information Continued */}
          <div className="border-t border-gray-200 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Active Status */}
              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    className="mr-2 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Active (visible to customers for bookings)
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Booking Duration */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-md font-semibold text-gray-900 mb-2">Booking Duration</h3>
            <p className="text-sm text-gray-500 mb-4">
              Configure minimum booking length and duration steps. Leave blank to use venue defaults.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Booking Duration
                </label>
                <select
                  value={formData.minBookingDurationHours}
                  onChange={(e) => handleChange('minBookingDurationHours', e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">Venue default</option>
                  <option value={0.5}>30 minutes</option>
                  <option value={1}>1 hour</option>
                  <option value={1.5}>1.5 hours</option>
                  <option value={2}>2 hours</option>
                  <option value={2.5}>2.5 hours</option>
                  <option value={3}>3 hours</option>
                  <option value={4}>4 hours</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration Step
                </label>
                <select
                  value={formData.bookingSlotIncrementHours}
                  onChange={(e) => handleChange('bookingSlotIncrementHours', e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">Default (30 min)</option>
                  <option value={0.5}>30 minutes</option>
                  <option value={1}>1 hour</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Customers can extend bookings in these increments
                </p>
              </div>
            </div>
          </div>

          {/* Recurring Bookings */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-md font-semibold text-gray-900 mb-4">Recurring Bookings</h3>
            <p className="text-sm text-gray-600 mb-4">
              Allow teams and regular groups to book this court for weekly/monthly recurring sessions with discounted rates.
            </p>

            {/* Enable Recurring Bookings Toggle */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.allowRecurringBookings}
                  onChange={(e) => handleChange('allowRecurringBookings', e.target.checked)}
                  className="mr-3 rounded border-gray-300 h-5 w-5"
                />
                <div>
                  <span className="text-sm font-semibold text-gray-900">
                    Enable Recurring Bookings
                  </span>
                  <p className="text-xs text-gray-600 mt-1">
                    Teams can book multiple sessions for 1-12 months
                  </p>
                </div>
              </label>
            </div>

            {/* Discount Inputs */}
            {formData.allowRecurringBookings && (
              <>
                <RecurringDiscountInputs
                  discount1Month={formData.discount1Month}
                  discount3Months={formData.discount3Months}
                  discount6Months={formData.discount6Months}
                  discount12Months={formData.discount12Months}
                  onChange={handleDiscountChange}
                  errors={errorMap}
                />
                <DiscountExamples
                  discount1Month={formData.discount1Month}
                  discount3Months={formData.discount3Months}
                  discount6Months={formData.discount6Months}
                  discount12Months={formData.discount12Months}
                />
              </>
            )}
          </div>

          {/* Court Sharing */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-md font-semibold text-gray-900 mb-4">Court Sharing (Optional)</h3>
            <p className="text-sm text-gray-600 mb-4">
              Configure parent-child court relationships for shared physical spaces (e.g., full football pitch split into 2 half-pitches).
            </p>

            <div className="space-y-4">
              {/* Share Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share Type
                </label>
                <select
                  value={formData.shareType}
                  onChange={(e) => handleChange('shareType', e.target.value as 'standalone' | 'shared_division' | 'cross_sport')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="standalone">Standalone (no sharing)</option>
                  <option value="shared_division">Shared Division (same sport, divided space)</option>
                  <option value="cross_sport">Cross Sport (different sports, same space)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.shareType === 'standalone' && 'This court does not share physical space with other courts'}
                  {formData.shareType === 'shared_division' && 'Example: Full Football Pitch → 2 Half Pitches'}
                  {formData.shareType === 'cross_sport' && 'Example: Basketball Court → 2 Tennis Courts'}
                </p>
              </div>

              {/* Physical Court ID - For Multi-Sport Courts */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Physical Court ID (for Multi-Sport Courts)
                </label>
                <input
                  type="text"
                  value={formData.physicalCourtId}
                  onChange={(e) => handleChange('physicalCourtId', e.target.value)}
                  placeholder="e.g., court-alpha, court-beta"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  <strong>Multi-Sport Courts:</strong> Assign the same ID to courts that share the same physical space across different sports.
                  <br />
                  <strong>Example:</strong> "court-alpha" for Basketball, Badminton, and Volleyball on the same physical court.
                  <br />
                  <strong>Half Courts:</strong> Use same ID for full court and its half courts.
                  <br />
                  Leave blank if this court doesn't share physical space with other sports.
                </p>
              </div>

              {/* Show parent court selection only if share type is not standalone */}
              {formData.shareType !== 'standalone' && (
                <>
                  {/* Parent Court Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Parent Court (optional)
                    </label>
                    <select
                      value={formData.parentCourtId ?? ''}
                      onChange={(e) => handleChange('parentCourtId', e.target.value === '' ? null : Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No parent (this is a parent court)</option>
                      {existingCourts
                        .filter((c) => c.id !== court?.id && !c.parent_court_id)
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select the parent court if this is a child court (e.g., "Half Pitch A" belongs to "Full Football Pitch")
                    </p>
                  </div>

                  {/* Is Virtual - Only for cross-sport */}
                  {formData.shareType === 'cross_sport' && (
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.isVirtual}
                          onChange={(e) => handleChange('isVirtual', e.target.checked)}
                          className="mr-2 rounded border-gray-300"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Virtual court (shares physical space with parent)
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        Check this if this court is a virtual representation on shared physical space (e.g., Tennis courts on a Basketball court)
                      </p>
                    </div>
                  )}

                  {/* Max Concurrent Children - Only if no parent selected */}
                  {!formData.parentCourtId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Concurrent Child Bookings (optional)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={formData.maxConcurrentChildren}
                        onChange={(e) => handleChange('maxConcurrentChildren', e.target.value === '' ? '' : Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 2"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Maximum number of child courts that can be booked at the same time (e.g., a full pitch divided into 2 halves = 2)
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
              <p className="text-xs text-amber-800">
                <strong>How Court Sharing Works:</strong><br />
                • When parent court is booked → all child courts become unavailable<br />
                • When child court is booked → parent and sibling courts become unavailable<br />
                • This prevents double-booking of the same physical space
              </p>
            </div>
          </div>

        </form>

        {/* Form Actions - Fixed at bottom */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white rounded-b-2xl flex items-center justify-end space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="court-form"
            disabled={isSaving}
            className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Court'}
          </button>
        </div>
    </Modal>
  );
}
